import React from "react";
import { Box } from "@chakra-ui/react";

export const JungleBackground: React.FC = () => {
  return (
    <Box
      position="absolute"
      top="0"
      left="0"
      width="100%"
      height="100%"
      overflow="hidden"
      zIndex="0"
      pointerEvents="none"
    >
      {/* Dense jungle sky gradient - filtered through canopy */}
      <Box
        position="absolute"
        width="100%"
        height="100%"
        background="linear-gradient(180deg, #0a2010 0%, #0d3015 20%, #15401d 40%, #1a5025 60%, #1d5a28 80%, #205a2a 100%)"
      />

      {/* Jungle SVG */}
      <svg
        className="jungle-svg"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <defs>
          {/* Leaf gradients */}
          <linearGradient id="leafDark" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1a4020" stopOpacity="1" />
            <stop offset="100%" stopColor="#0d2810" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="leafMid" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#2d6030" stopOpacity="1" />
            <stop offset="100%" stopColor="#1a4020" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="leafLight" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#408040" stopOpacity="1" />
            <stop offset="100%" stopColor="#2d6030" stopOpacity="1" />
          </linearGradient>
          <linearGradient id="leafBright" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#50a050" stopOpacity="1" />
            <stop offset="100%" stopColor="#408040" stopOpacity="1" />
          </linearGradient>

          {/* Vine gradient */}
          <linearGradient id="vineGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3d5030" stopOpacity="1" />
            <stop offset="50%" stopColor="#4a6038" stopOpacity="1" />
            <stop offset="100%" stopColor="#3d5030" stopOpacity="1" />
          </linearGradient>

          {/* Tree trunk gradient */}
          <linearGradient id="trunkGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#3d2815" stopOpacity="1" />
            <stop offset="50%" stopColor="#5a4020" stopOpacity="1" />
            <stop offset="100%" stopColor="#3d2815" stopOpacity="1" />
          </linearGradient>

          {/* Light ray gradient */}
          <linearGradient id="lightRay" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor="#ffffaa" stopOpacity="0.15" />
            <stop offset="100%" stopColor="#ffffaa" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Background foliage layer - darkest */}
        <g className="foliage-back">
          {/* Large palm fronds - back */}
          <path className="palm-back-1" d="M-50,200 Q50,100 150,150 Q100,200 -50,200" fill="url(#leafDark)" />
          <path className="palm-back-2" d="M100,50 Q200,0 250,80 Q180,120 100,50" fill="url(#leafDark)" />
          <path className="palm-back-3" d="M300,100 Q400,30 480,90 Q400,150 300,100" fill="url(#leafDark)" />
          <path className="palm-back-4" d="M600,60 Q700,0 780,70 Q700,130 600,60" fill="url(#leafDark)" />
          <path className="palm-back-5" d="M850,80 Q950,20 1020,100 Q940,150 850,80" fill="url(#leafDark)" />
          <path className="palm-back-6" d="M1100,50 Q1200,-10 1280,60 Q1200,120 1100,50" fill="url(#leafDark)" />
          <path className="palm-back-7" d="M1300,120 Q1400,50 1500,130 Q1400,180 1300,120" fill="url(#leafDark)" />
        </g>

        {/* Mid foliage layer */}
        <g className="foliage-mid">
          {/* Banana leaves */}
          <ellipse className="banana-leaf-1" cx="80" cy="300" rx="80" ry="25" fill="url(#leafMid)" transform="rotate(-20 80 300)" />
          <ellipse className="banana-leaf-2" cx="250" cy="200" rx="90" ry="28" fill="url(#leafMid)" transform="rotate(15 250 200)" />
          <ellipse className="banana-leaf-3" cx="500" cy="250" rx="85" ry="26" fill="url(#leafMid)" transform="rotate(-10 500 250)" />
          <ellipse className="banana-leaf-4" cx="750" cy="180" rx="95" ry="30" fill="url(#leafMid)" transform="rotate(20 750 180)" />
          <ellipse className="banana-leaf-5" cx="1000" cy="220" rx="80" ry="25" fill="url(#leafMid)" transform="rotate(-15 1000 220)" />
          <ellipse className="banana-leaf-6" cx="1200" cy="280" rx="90" ry="28" fill="url(#leafMid)" transform="rotate(10 1200 280)" />
          <ellipse className="banana-leaf-7" cx="1400" cy="200" rx="85" ry="26" fill="url(#leafMid)" transform="rotate(-25 1400 200)" />
        </g>

        {/* Tree trunks - organic curved shapes extending full height */}
        <g className="tree-trunks">
          {/* Trunk 1 - curves left then right */}
          <path d="M70,-50 Q50,150 65,350 Q45,550 70,700 Q85,850 60,950 L100,950 Q120,850 95,700 Q75,550 95,350 Q80,150 100,-50 Z" fill="url(#trunkGrad)" />
          {/* Trunk 2 - slight lean right */}
          <path d="M350,-50 Q370,150 355,350 Q360,550 345,700 Q330,850 355,950 L390,950 Q405,850 385,700 Q375,550 395,350 Q380,150 395,-50 Z" fill="url(#trunkGrad)" />
          {/* Trunk 3 - gentle S curve */}
          <path d="M710,-50 Q680,150 720,350 Q670,500 710,600 Q740,720 705,830 Q680,920 720,950 L760,950 Q790,920 755,830 Q730,720 760,600 Q785,500 750,350 Q780,150 750,-50 Z" fill="url(#trunkGrad)" />
          {/* Trunk 4 - curves right */}
          <path d="M1055,-50 Q1080,150 1050,350 Q1070,550 1050,700 Q1035,850 1060,950 L1095,950 Q1110,850 1090,700 Q1080,550 1100,350 Q1085,150 1100,-50 Z" fill="url(#trunkGrad)" />
          {/* Trunk 5 - leans left with bulge */}
          <path d="M1375,-50 Q1350,150 1380,350 Q1340,500 1365,650 Q1385,780 1350,870 Q1335,920 1355,950 L1400,950 Q1415,920 1395,870 Q1370,780 1405,650 Q1425,500 1395,350 Q1420,150 1405,-50 Z" fill="url(#trunkGrad)" />
        </g>

        {/* Hanging vines */}
        <g className="vines">
          <path className="vine-1" d="M150,0 Q140,150 170,300 Q150,450 180,600" stroke="url(#vineGrad)" strokeWidth="6" fill="none" />
          <path className="vine-2" d="M400,0 Q420,180 390,360 Q410,540 380,720" stroke="url(#vineGrad)" strokeWidth="5" fill="none" />
          <path className="vine-3" d="M650,0 Q630,200 670,400 Q640,600 680,800" stroke="url(#vineGrad)" strokeWidth="7" fill="none" />
          <path className="vine-4" d="M900,0 Q920,150 890,300 Q910,450 880,600" stroke="url(#vineGrad)" strokeWidth="5" fill="none" />
          <path className="vine-5" d="M1150,0 Q1130,180 1170,360 Q1140,540 1180,720" stroke="url(#vineGrad)" strokeWidth="6" fill="none" />
          <path className="vine-6" d="M1380,0 Q1400,200 1370,400 Q1390,600 1360,800" stroke="url(#vineGrad)" strokeWidth="5" fill="none" />
        </g>

        {/* Foreground foliage - brightest */}
        <g className="foliage-front">
          {/* Large fern fronds from sides */}
          <path className="fern-1" d="M-30,500 Q80,400 180,450 Q80,520 -30,500" fill="url(#leafLight)" />
          <path className="fern-2" d="M-20,650 Q100,550 200,620 Q100,700 -20,650" fill="url(#leafBright)" />
          <path className="fern-3" d="M1260,480 Q1360,380 1470,440 Q1360,520 1260,480" fill="url(#leafLight)" />
          <path className="fern-4" d="M1280,630 Q1380,530 1490,600 Q1380,680 1280,630" fill="url(#leafBright)" />

          {/* Bottom dense foliage */}
          <ellipse cx="100" cy="850" rx="150" ry="80" fill="url(#leafLight)" />
          <ellipse cx="350" cy="870" rx="180" ry="90" fill="url(#leafMid)" />
          <ellipse cx="600" cy="840" rx="160" ry="85" fill="url(#leafLight)" />
          <ellipse cx="850" cy="860" rx="170" ry="88" fill="url(#leafMid)" />
          <ellipse cx="1100" cy="850" rx="155" ry="82" fill="url(#leafLight)" />
          <ellipse cx="1350" cy="870" rx="175" ry="90" fill="url(#leafMid)" />
        </g>

        {/* Light rays through canopy */}
        <g className="light-rays">
          <polygon className="ray-1" points="200,0 280,0 350,900 200,900" fill="url(#lightRay)" />
          <polygon className="ray-2" points="550,0 610,0 680,900 500,900" fill="url(#lightRay)" />
          <polygon className="ray-3" points="900,0 970,0 1050,900 880,900" fill="url(#lightRay)" />
          <polygon className="ray-4" points="1200,0 1260,0 1340,900 1180,900" fill="url(#lightRay)" />
        </g>

        {/* Small leaves and details */}
        <g className="small-leaves">
          <ellipse cx="180" cy="400" rx="20" ry="8" fill="#3a7035" className="small-leaf-1" />
          <ellipse cx="420" cy="320" rx="18" ry="7" fill="#458040" className="small-leaf-2" />
          <ellipse cx="580" cy="450" rx="22" ry="9" fill="#3a7035" className="small-leaf-3" />
          <ellipse cx="780" cy="350" rx="19" ry="7" fill="#458040" className="small-leaf-4" />
          <ellipse cx="950" cy="420" rx="21" ry="8" fill="#3a7035" className="small-leaf-5" />
          <ellipse cx="1120" cy="380" rx="18" ry="7" fill="#458040" className="small-leaf-6" />
          <ellipse cx="1300" cy="450" rx="20" ry="8" fill="#3a7035" className="small-leaf-7" />
        </g>
      </svg>

      {/* Floating particles - pollen/spores */}
      <Box className="jungle-particles" position="absolute" width="100%" height="100%" top="0" left="0">
        {[...Array(15)].map((_, i) => (
          <Box
            key={i}
            className={`jungle-particle jungle-particle-${i + 1}`}
            position="absolute"
            width={3 + (i % 3) + "px"}
            height={3 + (i % 3) + "px"}
            borderRadius="50%"
            backgroundColor={i % 2 === 0 ? "#a0d080" : "#80c060"}
            opacity="0"
          />
        ))}
      </Box>

      {/* Ambient mist/fog */}
      <Box
        className="jungle-mist-1"
        position="absolute"
        bottom="10%"
        left="0"
        width="100%"
        height="200px"
        background="linear-gradient(180deg, transparent 0%, rgba(100,140,80,0.1) 50%, rgba(80,120,60,0.15) 100%)"
      />
      <Box
        className="jungle-mist-2"
        position="absolute"
        bottom="0"
        left="0"
        width="100%"
        height="150px"
        background="linear-gradient(180deg, transparent 0%, rgba(60,100,50,0.2) 100%)"
      />
    </Box>
  );
};

export default JungleBackground;
